import {
  FormController,
  FormFileUpload,
  FormInput,
  FormTextArea,
} from '@/components/form';
import { QualityControlFeatures } from '@/features';

import { PermissionCheck } from '@/components';
import { QualityControlApi } from '@/state/api';
import { QualityControlType } from '@/types';
import { permissionsKeys } from '@/types/permissionsType';
import {
  Badge,
  Box,
  Button,
  FileUploadFileMimeType,
  Flex,
  Grid,
  GridItem,
  Separator,
  Span,
  Text,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  AttachmentFile,
  OverlayDialog,
  toasterApi,
  ViewAttachmentsCard,
} from '@ui';
import dayjs from 'dayjs';
import React, { ReactNode, Suspense } from 'react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { FaArrowLeftLong } from 'react-icons/fa6';
import z from 'zod';

const ViewAttachments = React.lazy(() => import('@ui/view-attachment'));

type EventDetailProps = {
  event_id?: number;
  handleGoBack: () => void;
};

const STATUS_COLOR: Record<string, string> = {
  OPEN: 'orange',
  CLOSED: 'green',
};

const SEVERITY_COLOR: Record<string, string> = {
  LOW: 'gray',
  MEDIUM: 'yellow',
  HIGH: 'orange',
  CRITICAL: 'red',
};

const RESULT_COLOR: Record<string, string> = {
  PASS: 'green',
  FAIL: 'red',
  INFO: 'blue',
};

const formatDate = (value?: string | Date | null, withTime = false) => {
  if (!value) return 'N/A';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return 'N/A';
  return parsed.format(withTime ? 'ddd, DD MMM YYYY, hh:mm A' : 'DD MMM YYYY');
};

const Field = ({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value?: ReactNode;
  multiline?: boolean;
}) => {
  const isEmpty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '');

  return (
    <Flex flexDirection="column" gapY={1}>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
      <Text
        fontSize="sm"
        color={isEmpty ? 'fg.muted' : 'fg'}
        whiteSpace={multiline ? 'pre-wrap' : 'normal'}
        wordBreak="break-word"
      >
        {isEmpty ? 'N/A' : value}
      </Text>
    </Flex>
  );
};

const Section = ({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) => (
  <Box
    borderWidth="1px"
    borderColor="border.emphasized"
    borderRadius="lg"
    p={5}
    bg="bg.subtle"
  >
    <Flex justifyContent="space-between" alignItems="center" mb={4}>
      <Text fontWeight="bold" fontSize="md">
        {title}
      </Text>
      {action}
    </Flex>
    {children}
  </Box>
);

const EventDetail = ({ event_id, handleGoBack }: EventDetailProps) => {
  const [patchEventAttachmentByIdMutation] =
    QualityControlApi.Api.usePatchEventAttachmentByIdMutation();

  const [patchUpdateCloseEventMutation] =
    QualityControlApi.Api.usePatchUpdateCloseEventMutation();

  const { data: getEventById } = QualityControlApi.Api.useGetEventByIdQuery(
    event_id
      ? {
          params: {
            id: event_id,
          },
        }
      : skipToken,
  );

  // Delete attachment mutation
  const [deleteEventAttachmentByIdMutation] =
    QualityControlApi.Api.useDeleteEventAttachmentByIdMutation();

  const handleSubmitAttachments = () => {
    OverlayDialog.open('qualityControlEventAttachments', {
      overlayId: 'qualityControlEventAttachments',
      title: 'Add Event Attachments',
      content: (
        <FormController
          size="lg"
          formSchema={fileUploadFormSchema}
          defaultValues={{
            attachmentFiles: [],
          }}
          onFormSubmit={async (formData) => {
            if (!formData?.attachmentFiles?.length || !event_id) {
              OverlayDialog.close('qualityControlEventAttachments');
              return;
            }

            await patchEventAttachmentByIdMutation({
              params: {
                id: event_id,
              },
              body: formData?.attachmentFiles,
            }).unwrap();

            try {
              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'Attachments added successfully',
              });
            } catch {
              toasterApi({
                type: 'error',
                status: 500,
                code: 'Error',
                message: 'Failed to add one or more attachments',
              });
            } finally {
              OverlayDialog.close('qualityControlEventAttachments');
            }
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<FileUploadFormType>;
            control: Control<FileUploadFormType>;
            errors: FieldErrors<FileUploadFormType>;
          }) => {
            return (
              <Flex flexDirection="column" gapY={8}>
                <Flex flexDirection="column" gapY={4}>
                  <Text fontSize="md" fontWeight="bold">
                    Upload Attachment Files
                  </Text>
                  <FormFileUpload
                    control={control}
                    register={register}
                    errors={errors}
                    id="attachmentFiles"
                    label=""
                    maxFiles={10}
                    accept={
                      [
                        'application/pdf',
                        'application/msword',
                        'image/*',
                      ] as FileUploadFileMimeType[]
                    }
                  />
                </Flex>
              </Flex>
            );
          }}
        </FormController>
      ),
    });
  };

  const handleSubmitPatchUpdateCloseEvent = () => {
    OverlayDialog.open('qualityControlPatchUpdateClose', {
      overlayId: 'qualityControlPatchUpdateClose',
      title: 'Close QC Event',
      description:
        'Record the closure details for this quality event. These notes are kept as audit data and shown after the event is closed.',
      content: (
        <FormController
          size="lg"
          formSchema={patchUpdateCloseEventMutationSchema}
          defaultValues={{
            corrective_action: getEventById?.corrective_action ?? '',
            preventive_action: getEventById?.preventive_action ?? '',
            closure_notes: getEventById?.closure_notes ?? '',
            immediate_action: getEventById?.immediate_action ?? '',
            frame_tag: getEventById?.frame_tag ?? '',
          }}
          onFormSubmit={async (formData) => {
            try {
              await patchUpdateCloseEventMutation({
                params: {
                  id: event_id!,
                },
                body: formData,
              }).unwrap();

              toasterApi({
                type: 'success',
                status: 200,
                code: 'Success',
                message: 'QC event closed successfully',
              });
            } catch {
              toasterApi({
                type: 'error',
                status: 500,
                code: 'Error',
                message: 'Failed to close the QC event',
              });
            } finally {
              OverlayDialog.close('qualityControlPatchUpdateClose');
            }
          }}
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<PatchUpdateCloseEventMutationType>;
            control: Control<PatchUpdateCloseEventMutationType>;
            errors: FieldErrors<PatchUpdateCloseEventMutationType>;
          }) => {
            return (
              <Flex flexDirection="column" gapY={4}>
                <Text fontSize="xs" color="fg.muted">
                  Fields marked * are required.
                </Text>
                <Flex gapX={4}>
                  <FormInput
                    label="Immediate Action"
                    id="immediate_action"
                    register={register}
                    control={control}
                    errors={errors}
                    showLabelAtTop
                    required
                  />
                  <FormInput
                    label="Corrective Action"
                    id="corrective_action"
                    register={register}
                    control={control}
                    errors={errors}
                    showLabelAtTop
                    required
                  />
                </Flex>
                <Flex gapX={4}>
                  <FormInput
                    label="Preventive Action"
                    id="preventive_action"
                    register={register}
                    control={control}
                    errors={errors}
                    showLabelAtTop
                    required
                  />
                  <FormInput
                    label="Frame Tag"
                    id="frame_tag"
                    register={register}
                    control={control}
                    errors={errors}
                    showLabelAtTop
                    required
                  />
                </Flex>
                <FormTextArea
                  register={register}
                  control={control}
                  errors={errors}
                  label="Closure Notes"
                  id="closure_notes"
                  required
                  showLabelAtTop
                  minH="5lh"
                  maxH="5lh"
                />
              </Flex>
            );
          }}
        </FormController>
      ),
    });
  };

  const handleEditQCEvent = ({ event_id }: { event_id: number }) => {
    OverlayDialog.open('editQCEvent', {
      overlayId: 'editQCEvent',
      title: 'Edit QC Event',
      description: 'Edit a quality control event.',
      content: (
        <QualityControlFeatures.ManageCard
          isNew={false}
          event_id={event_id}
          onClose={() => {
            OverlayDialog.close('editQCEvent');
          }}
        />
      ),
    });
  };

  const handleDeleteQualityControlAttachmentMutation = ({
    id,
    filename,
    file,
  }: AttachmentFile) => {
    OverlayDialog.open('deleteQualityControlAttachment', {
      overlayId: 'deleteQualityControlAttachment',
      title: 'Delete Quality Control Attachment',
      description: (
        <Flex flexDirection="column" gapY={2}>
          <Text>
            Are you sure you want to delete this attachment? This action is
            irreversible.
          </Text>
          <Flex>
            <Span textStyle="heading.title" color="secondary.fg">
              File name:&nbsp;
            </Span>
            <Span>{filename}</Span>
          </Flex>
        </Flex>
      ),

      content: <ViewAttachmentsCard filename={filename} file={file} />,
      handleConfirm: () => {
        deleteEventAttachmentByIdMutation({
          params: {
            id,
          },
        })
          .unwrap()
          .then(() => {
            toasterApi({
              type: 'success',
              status: 204,
              code: 'Success',
              message: 'Attachment deleted successfully',
            });
          })
          .catch(() => {
            toasterApi({
              type: 'error',
              status: 500,
              code: 'Error',
              message: 'Could not delete attachment',
            });
          })
          .finally(() => {
            OverlayDialog.close('deleteQualityControlAttachment');
          });
      },
    });
  };

  const handleOpenQualityControlAttachmentMutation = ({
    filename,
    file,
  }: AttachmentFile) => {
    OverlayDialog.open('viewQualityControlAttachment', {
      overlayId: 'viewQualityControlAttachment',
      title: 'View Quality Control Attachment',
      description: (
        <Flex alignItems="center">
          <Span textStyle="heading.title" color="secondary.fg">
            File name:&nbsp;
          </Span>
          <Span>{filename}</Span>
        </Flex>
      ),
      content: <ViewAttachmentsCard filename={filename} file={file} />,
    });
  };

  const isClosed = getEventById?.status === 'CLOSED';

  return (
    <Flex flexDirection="column" gapY={6}>
      {/* Action bar */}
      <Flex justifyContent="space-between" alignItems="center">
        <Button
          onClick={handleGoBack}
          variant="outline"
          color="fg"
          borderColor="border.emphasized"
        >
          <FaArrowLeftLong />
          Back
        </Button>
        <PermissionCheck permissions={[permissionsKeys.change_qualityevent]}>
          <Flex gapX={2}>
            <Button
              disabled={isClosed || !event_id}
              onClick={() => event_id && handleEditQCEvent({ event_id })}
              variant="outline"
              color="fg"
              borderColor="border.emphasized"
            >
              Edit Event
            </Button>

            <Button
              disabled={isClosed}
              variant="outline"
              color="fg"
              borderColor="border.emphasized"
              onClick={handleSubmitAttachments}
            >
              Add Attachments
            </Button>

            <Button
              disabled={isClosed}
              colorPalette="green"
              onClick={handleSubmitPatchUpdateCloseEvent}
            >
              Mark as Closed
            </Button>
          </Flex>
        </PermissionCheck>
      </Flex>

      {/* Summary header */}
      <Section title="Summary">
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gapY={3}
        >
          <Flex flexDirection="column" gapY={2}>
            <Flex alignItems="center" gapX={3} flexWrap="wrap">
              <Text fontSize="lg" fontWeight="bold">
                {getEventById?.qc_number ?? 'QC Event'}
              </Text>
              {getEventById?.status && (
                <Badge
                  colorPalette={STATUS_COLOR[getEventById.status] ?? 'gray'}
                >
                  {getEventById.status}
                </Badge>
              )}
              {getEventById?.severity && (
                <Badge
                  colorPalette={SEVERITY_COLOR[getEventById.severity] ?? 'gray'}
                  variant="subtle"
                >
                  {getEventById.severity}
                </Badge>
              )}
              {getEventById?.result && (
                <Badge
                  colorPalette={RESULT_COLOR[getEventById.result] ?? 'gray'}
                  variant="outline"
                >
                  {getEventById.result}
                </Badge>
              )}
            </Flex>
            <Text fontSize="sm" color="fg.muted">
              {getEventById?.module} · {getEventById?.project_name} /{' '}
              {getEventById?.release_number}
            </Text>
          </Flex>
        </Flex>
      </Section>

      {/* Issue details */}
      <Section title="Issue Details">
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
          <GridItem gridColumn="1 / -1">
            <Field
              label="Description"
              value={getEventById?.description}
              multiline
            />
          </GridItem>
          <Field label="Module" value={getEventById?.module} />
          <Field label="Severity" value={getEventById?.severity} />
          <Field label="Result" value={getEventById?.result} />
          <Field label="Quantity Affected" value={getEventById?.qty_affected} />
          <Field
            label="Specification Reference"
            value={getEventById?.spec_ref}
          />
          <Field label="Detected By" value={getEventById?.detected_by_name} />
          <Field
            label="Detected At"
            value={formatDate(getEventById?.detected_at, true)}
          />
          <Field label="Due Date" value={formatDate(getEventById?.due_date)} />
        </Grid>
      </Section>

      {/* Related items */}
      <Section title="Related Items">
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
          <Field label="Project" value={getEventById?.project_name} />
          <Field label="Release" value={getEventById?.release_number} />
          <Field
            label="Work Order"
            value={
              getEventById?.work_order_identifier ??
              getEventById?.work_order ??
              undefined
            }
          />
          <Field label="Frame Tag" value={getEventById?.frame_tag} />
          <Field label="Part Number" value={getEventById?.part_number} />
          <Field
            label="Purchase Order"
            value={getEventById?.purchase_order_number}
          />
          <Field
            label="Reference PO Number"
            value={getEventById?.purchase_order_reference}
          />
          <Field label="Supplier" value={getEventById?.supplier_name} />
          <Field label="Location" value={getEventById?.location} />
        </Grid>
      </Section>

      {/* Closure / resolution */}
      <Section title="Closure & Resolution">
        {isClosed ? (
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
            <Field label="Closed By" value={getEventById?.closed_by_name} />
            <Field
              label="Closed On"
              value={formatDate(getEventById?.closed_on as never)}
            />
            <Field label="Disposition" value={getEventById?.disposition} />
            <GridItem gridColumn="1 / -1">
              <Field
                label="Closure Notes"
                value={getEventById?.closure_notes}
                multiline
              />
            </GridItem>
            <Field
              label="Immediate Action"
              value={getEventById?.immediate_action}
              multiline
            />
            <Field
              label="Corrective Action"
              value={getEventById?.corrective_action}
              multiline
            />
            <Field
              label="Preventive Action"
              value={getEventById?.preventive_action}
              multiline
            />
            <Field
              label="Root Cause"
              value={getEventById?.root_cause}
              multiline
            />
            <Field
              label="Root Cause Category"
              value={getEventById?.root_cause_category}
            />
          </Grid>
        ) : (
          <Text fontSize="sm" color="fg.muted">
            This event is still open. Closure details will appear here once the
            event is marked as closed.
          </Text>
        )}
      </Section>

      {/* History */}
      <Section title="History">
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
          <Field label="Created By" value={getEventById?.created_by_name} />
          <Field
            label="Created At"
            value={formatDate(getEventById?.created_at as never, true)}
          />
          <Field
            label="Last Updated"
            value={formatDate(getEventById?.updated_at as never, true)}
          />
        </Grid>
      </Section>

      {/* Attachments */}
      <Section title="Attachments">
        <Separator mb={4} />
        <Box width="full">
          <Suspense fallback={<Text>Loading attachments...</Text>}>
            <ViewAttachments
              attachments={getEventById?.attachments ?? []}
              onDelete={(props) =>
                handleDeleteQualityControlAttachmentMutation({
                  id: props.id,
                  filename: props.filename,
                  file: props.file,
                })
              }
              onSelect={(props) =>
                handleOpenQualityControlAttachmentMutation({
                  id: props.id,
                  filename: props.filename,
                  file: props.file,
                })
              }
            />
          </Suspense>
        </Box>
      </Section>
    </Flex>
  );
};
export default EventDetail;

const fileUploadFormSchema = z.object({
  attachmentFiles: z.array(z.instanceof(File)).optional(),
});
type FileUploadFormType = z.infer<typeof fileUploadFormSchema>;

const patchUpdateCloseEventMutationSchema =
  QualityControlType.closeEventBaseSchema;
type PatchUpdateCloseEventMutationType = z.infer<
  typeof patchUpdateCloseEventMutationSchema
>;
